// tickets-csv-export (design D4/D6, spec "Ticket Export Specification") —
// Ticket Export API client
//
// createTicketExport(filters) → POST /admin/tickets/exports (org_admin | supervisor)
//   Accepts the SAME filter set as browseTickets (minus pagination), snapshots
//   them + the actor's scope server-side, returns 202 + jobId. 409 when the
//   org already has an active (pending|processing) export.
//
// getTicketExport(jobId) → GET /admin/tickets/exports/:id
//   Returns job status and, once `done`, a presigned S3 download URL.
//
// Mirrors ticket-browse.ts's shape (runtime-validated response via the
// shared Zod schema).

import type { CreateTicketExportRequest, TicketExportJobResponse } from '@jq4race/shared';
import { CreateTicketExportResponseSchema, TicketExportJobResponseSchema } from '@jq4race/shared';

import { request } from './client.js';

/**
 * POST /admin/tickets/exports
 *
 * Kicks off an async, streaming CSV export scoped identically to
 * GET /admin/tickets. Returns immediately with a jobId to poll via
 * getTicketExport(). Rejects with 409 (via ApiError) when the org already
 * has an active export in flight.
 */
export async function createTicketExport(
  filters: CreateTicketExportRequest,
): Promise<{ jobId: string }> {
  const payload = await request<unknown>('/admin/tickets/exports', {
    method: 'POST',
    body: filters,
  });
  return CreateTicketExportResponseSchema.parse(payload);
}

/**
 * GET /admin/tickets/exports/:id
 *
 * Returns the job's current status. When status is 'done', `downloadUrl`
 * holds a presigned S3 GET link; when 'failed', `error` holds the failure
 * reason. Callers are expected to poll until a terminal state is reached.
 */
export async function getTicketExport(jobId: string): Promise<TicketExportJobResponse> {
  const payload = await request<unknown>(`/admin/tickets/exports/${encodeURIComponent(jobId)}`, {
    method: 'GET',
  });
  return TicketExportJobResponseSchema.parse(payload);
}
